import Avatar from '@atlaskit/avatar';
import Tooltip from '@atlaskit/tooltip';
import React, { memo, useRef, type ReactNode } from 'react';
import { type UserReadContent } from './user-read-content.ts';
import styles from './user-value-view.module.css';

export type UserAvatarWrapper = (accountId: string, avatar: ReactNode) => ReactNode;

/**
 * Measure only when the tooltip opens; no per-value observer or React state.
 */
function UserLabel({ children }: { readonly children: string }) {
  const ref = useRef<HTMLSpanElement>(null);

  return (
    <span ref={ref} className={styles.label}>
      <Tooltip
        content={children}
        canAppear={() =>
          !!ref.current && ref.current.offsetWidth < ref.current.scrollWidth
        }
        tag="span"
      >
        <span>{children}</span>
      </Tooltip>
    </span>
  );
}

/**
 * Field-owned avatar rows; application composition supplies optional profile presentation.
 */
export const UserValueView = memo(function UserValueView({
  content,
  wrapAvatar
}: {
  readonly content: UserReadContent;
  readonly wrapAvatar?: UserAvatarWrapper;
}) {
  return (
    <ul className={styles.value}>
      {content.users.map((user) => {
        const avatar = (
          <Avatar
            appearance="circle"
            borderColor="none"
            name={user.avatarName}
            size="small"
            src={user.avatarUrl}
            testId="table-v2-user-avatar"
          />
        );

        return (
          <li key={user.key}>
            {user.displayName !== undefined ? (
              <div className={styles.row}>
                {wrapAvatar ? wrapAvatar(user.accountId, avatar) : avatar}
                <UserLabel>{user.displayName}</UserLabel>
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
});
